import { ModulesGroup } from "./shared/module-description";
import { UserAuthModule } from "./student/auth/model";
import { SignInFormModule } from "./student/auth/ui/sign-in.form";
import { CoursesModule } from "@features/courses/model";
import { CurrentCourseModule } from "@features/courses/ui/current";
import { EnrollmentsModule } from "@features/enrollments/model";
import { TopicsModule } from "@features/topics/model";
import { LessonsModule } from "@features/lessons/model";
import { TasksModule } from "@features/tasks/model";
import { CurrentTopicModule } from "@features/topics/ui/current";
import { CurrentLessonModule } from "@features/lessons/ui/current";
import { RouterModule } from "@features/shared/router";
import { TokenStorageModule } from "@features/shared/tokenStorage";
import { NotificationModule } from "@features/notification/model";
import { TipsModule } from "./tips/model";
import { CurrentTipModule } from "./tips/ui/current";
import { OnboardingModule } from "./onboarding/model";
import { CurrentOnboardingScreenModule } from "./onboarding/ui/current";
import { PaymentModule } from "./payment/ui";
import { LandingModule } from "./landing/model";
import { ProfessionFormModule } from "./landing/ui/main-landing";
import { SubscriptionModule } from "./subscription/model";
import { PlatformContextModule } from "@features/launch-context/model";
import { FlagsmithModule } from "./flagsmith/model";
import { LuckLandingModule } from "./landing/ui/luck-landing";
import { UpsellUIModule } from "./upsell/ui";
import { UpsellModule } from "./upsell/model";
import { ProductOnboardingModule } from "./onboarding/ui/productOnboarding";
import { CreatorLandingModule } from "./landing/ui/creator-landing";
import { StreakModule } from "./streak/model";
import { StreakWidgetModule } from "./streak/ui/streak-widget";
import { DailyGoalStreakModule } from "./streak/ui/daily-goal-streak";
import { ProductOnboardingEventsModule } from "./onboarding/model/product-onboarding-events";
import { MetadataModule } from "./student/metadata/model";
import { DailyTaskModule } from "./daily-task/model";
import { DailyTaskUiModule } from "./daily-task/ui";
import { FilteredCoursesModule } from "./courses/ui/filtered-courses";
import { LastEnrollmentModule } from "./courses/ui/last-enrollment";
import { RoadmapsModule } from "./roadmaps/model";
// Evaluated here, not with the /ai-tools and /assistant chunks: the card model listens to
// getRoadmapsConfigFx, which settles right after login, usually before AI tools is opened.
import "@features/ai-tools/profession-cards";
import { RoadmapCertificateProgramModule } from "./roadmaps/ui/certificate-program";
import { AssistantUIModule } from "@features/assistant/ui";
import { AssistantModelModule } from "@features/assistant/model";
import { PageTypeModule } from "@features/shared/page-type";
import { RegistrationModule } from "./student/auth/ui/registration";
import { LessonFeedbackModule } from "./lessons/ui/lesson-feedback";
import { LessonCompletionModule } from "./lesson-completion/model";
import { LessonCompletionFlowModule } from "./lesson-completion/ui";
import { SoundModule } from "./sound/model";
import { LessonTransitionModule } from "./lesson-completion/ui/transition";
import { XpModule } from "./xp/model";
import { ThemeModule } from "./theme/model";
import { LessonExitConfirmationUiModule } from "./lessons/ui/lesson-exit-confirmation";
import { CertificateModalModule } from "@features/certificate/model";
import { AmplitudeFlagsModule } from "@features/amplitude-flags/model";
import { UltimateAiPackModule } from "./upsell/ui/ultimate-ai-pack";
import { PersonalBoostPackModule } from "./upsell/ui/personal-boost-pack";
import { AiToolsPackModule } from "./upsell/ui/ai-tools-pack";
import { ClaudeWorkBundleModule } from "./upsell/ui/claude-work-bundle";
import { CompleteBundleModule } from "./upsell/ui/complete-bundle";
import { CreativePacksModule } from "./upsell/ui/creative-packs";
import { UpsellAnalyticsModule } from "./upsell/model/analytics";
import { LessonUiModule } from "./lessons/ui";
import { TaskLayoutUIModule } from "@features/tasks/ui";
import { InteractivePromptModelModule } from "./interactive-prompt/model";
import { InteractivePromptUIModule } from "./interactive-prompt/ui";
import { CompleteAiPackModule } from "./upsell/ui/complete-ai-pack";
import { AppModule } from "./app/model";
import { AppRouteUnitsModule } from "./app/route-units";
import { ChallengesModule } from "./courses/ui/challenges";
import { PromptsLibraryModule } from "./prompts-library/model";
import { PromptsLibraryUIModule } from "./prompts-library/ui";
import { PromptsLibraryAnalyticsModule } from "./prompts-library/model/analytics";
import { BrowserInstructionModule } from "./browserInstruction/model";
import { ReEngagementModule } from "./re-engagement/model";
import { AppTourModule } from "./app-tour/model";

const userAuthModule = new UserAuthModule();
const appRoutesModule = new AppRouteUnitsModule();
const platformContextModule = new PlatformContextModule(appRoutesModule.getContract());
const routerModule = new RouterModule();
const flagsmithModule = new FlagsmithModule();
const amplitudeFlagsModule = new AmplitudeFlagsModule(userAuthModule.getContract());
const signInFormModule = new SignInFormModule(userAuthModule.getContract());
const coursesModule = new CoursesModule(
    userAuthModule.getContract(),
    platformContextModule.getContract(),
    amplitudeFlagsModule.getContract()
);
const enrollmentsModule = new EnrollmentsModule(userAuthModule.getContract());
const currentCourseModule = new CurrentCourseModule(
    userAuthModule.getContract(),
    coursesModule.getContract(),
    enrollmentsModule.getContract()
);
const topicsModule = new TopicsModule(
    userAuthModule.getContract(),
    currentCourseModule.getContract(),
    enrollmentsModule.getContract(),
    platformContextModule.getContract()
);
const currentTopicModule = new CurrentTopicModule(
    userAuthModule.getContract(),
    topicsModule.getContract(),
    enrollmentsModule.getContract()
);
const lessonsModule = new LessonsModule(
    userAuthModule.getContract(),
    currentTopicModule.getContract(),
    enrollmentsModule.getContract(),
    topicsModule.getContract()
);

enrollmentsModule.setDependency(lessonsModule.getContract());

const currentLessonModule = new CurrentLessonModule(
    userAuthModule.getContract(),
    lessonsModule.getContract(),
    enrollmentsModule.getContract(),
    routerModule.getContract(),
    currentCourseModule.getContract(),
    flagsmithModule.getContract()
);
const tasksModule = new TasksModule(
    userAuthModule.getContract(),
    currentLessonModule.getContract(),
    enrollmentsModule.getContract()
);
currentLessonModule.setDependency(tasksModule.getContract());

const notificationModule = new NotificationModule(appRoutesModule.getContract());
const pageTypeModule = new PageTypeModule();
const tokenStorageModule = new TokenStorageModule();
const appModule = new AppModule();
const aiTipsModule = new TipsModule(userAuthModule.getContract());
const currentAiTipModule = new CurrentTipModule(userAuthModule.getContract());
const onboardingModule = new OnboardingModule();
const paymentModule = new PaymentModule();
const landingModule = new LandingModule();
const landingProfessionFormModule = new ProfessionFormModule(landingModule.getContract());
const subscriptionModule = new SubscriptionModule(userAuthModule.getContract());
const creatorLandingModule = new CreatorLandingModule(
    paymentModule.getContract(),
    flagsmithModule.getContract(),
    routerModule.getContract()
);
const luckLandingModule = new LuckLandingModule(paymentModule.getContract(), flagsmithModule.getContract());
const currentOnboardingScreenModule = new CurrentOnboardingScreenModule(
    onboardingModule.getContract(),
    userAuthModule.getContract(),
    flagsmithModule.getContract(),
    paymentModule.getContract(),
    subscriptionModule.getContract(),
    routerModule.getContract(),
    appModule.getContract()
);
const metadataModule = new MetadataModule();

const productOnboardingModule = new ProductOnboardingModule(
    userAuthModule.getContract(),
    enrollmentsModule.getContract(),
    onboardingModule.getContract(),
    subscriptionModule.getContract(),
    metadataModule.getContract(),
    coursesModule.getContract(),
    amplitudeFlagsModule.getContract(),
    platformContextModule.getContract(),
    appModule.getContract()
);

const upsellModule = new UpsellModule(
    appRoutesModule.getContract(),
    appModule.getContract(),
    paymentModule.getContract(),
    subscriptionModule.getContract(),
    routerModule.getContract(),
    userAuthModule.getContract(),
    metadataModule.getContract(),
    signInFormModule.getContract(),
    platformContextModule.getContract(),
    amplitudeFlagsModule.getContract()
);

const upsellUIModule = new UpsellUIModule(
    routerModule.getContract(),
    productOnboardingModule.getContract(),
    upsellModule.getContract(),
    appRoutesModule.getContract(),
    amplitudeFlagsModule.getContract(),
    platformContextModule.getContract()
);

const streakModule = new StreakModule(userAuthModule.getContract(), lessonsModule.getContract());
const streakWidgetModule = new StreakWidgetModule(streakModule.getContract(), flagsmithModule.getContract());
const certificateModalModule = new CertificateModalModule(
    currentCourseModule.getContract(),
    userAuthModule.getContract(),
    coursesModule.getContract()
);
const productOnboardingEventsModule = new ProductOnboardingEventsModule(
    currentOnboardingScreenModule.getContract(),
    productOnboardingModule.getContract(),
    coursesModule.getContract()
);

const dailyTaskModule = new DailyTaskModule(userAuthModule.getContract());
const dailyTaskUiModule = new DailyTaskUiModule(
    dailyTaskModule.getContract(),
    userAuthModule.getContract(),
    metadataModule.getContract(),
    flagsmithModule.getContract()
);
const filteredCoursesModule = new FilteredCoursesModule(
    coursesModule.getContract(),
    enrollmentsModule.getContract(),
    routerModule.getContract()
);
const roadmapsModule = new RoadmapsModule(
    userAuthModule.getContract(),
    amplitudeFlagsModule.getContract(),
    metadataModule.getContract(),
    lessonsModule.getContract()
);
const roadmapCertificateProgramModule = new RoadmapCertificateProgramModule(
    roadmapsModule.getContract(),
    amplitudeFlagsModule.getContract(),
    routerModule.getContract(),
    certificateModalModule.getContract()
);
const dailyGoalStreakModule = new DailyGoalStreakModule(
    streakModule.getContract(),
    enrollmentsModule.getContract(),
    metadataModule.getContract(),
    lessonsModule.getContract(),
    userAuthModule.getContract(),
    coursesModule.getContract(),
    roadmapsModule.getContract()
);
const lessonUiModule = new LessonUiModule(
    lessonsModule.getContract(),
    currentLessonModule.getContract(),
    currentCourseModule.getContract(),
    enrollmentsModule.getContract(),
    tasksModule.getContract(),
    routerModule.getContract()
);
const lastEnrollmentModule = new LastEnrollmentModule(
    enrollmentsModule.getContract(),
    coursesModule.getContract(),
    filteredCoursesModule.getContract(),
    routerModule.getContract(),
    productOnboardingModule.getContract(),
    appRoutesModule.getContract(),
    metadataModule.getContract(),
    userAuthModule.getContract(),
    amplitudeFlagsModule.getContract(),
    lessonUiModule.getContract(),
    roadmapsModule.getContract()
);

const registrationModule = new RegistrationModule(
    userAuthModule.getContract(),
    routerModule.getContract(),
    signInFormModule.getContract()
);

const lessonFeedbackModule = new LessonFeedbackModule(
    currentCourseModule.getContract(),
    currentLessonModule.getContract(),
    amplitudeFlagsModule.getContract(),
    metadataModule.getContract(),
    appRoutesModule.getContract()
);
const taskLayoutUIModule = new TaskLayoutUIModule(
    tasksModule.getContract(),
    enrollmentsModule.getContract(),
    currentCourseModule.getContract(),
    currentTopicModule.getContract(),
    currentLessonModule.getContract()
);

const ultimateAiPackModule = new UltimateAiPackModule(
    userAuthModule.getContract(),
    routerModule.getContract(),
    paymentModule.getContract(),
    upsellModule.getContract(),
    upsellUIModule.getContract(),
    subscriptionModule.getContract(),
    appRoutesModule.getContract()
);

const personalBoostPackModule = new PersonalBoostPackModule(
    userAuthModule.getContract(),
    routerModule.getContract(),
    paymentModule.getContract(),
    upsellModule.getContract(),
    upsellUIModule.getContract(),
    subscriptionModule.getContract(),
    appRoutesModule.getContract()
);

const aiToolsPackModule = new AiToolsPackModule(
    userAuthModule.getContract(),
    routerModule.getContract(),
    paymentModule.getContract(),
    upsellModule.getContract(),
    upsellUIModule.getContract(),
    appModule.getContract(),
    subscriptionModule.getContract(),
    amplitudeFlagsModule.getContract(),
    appRoutesModule.getContract(),
    notificationModule.getContract(),
    metadataModule.getContract(),
    platformContextModule.getContract()
);

const claudeWorkBundleModule = new ClaudeWorkBundleModule(
    userAuthModule.getContract(),
    routerModule.getContract(),
    paymentModule.getContract(),
    upsellModule.getContract(),
    upsellUIModule.getContract(),
    subscriptionModule.getContract(),
    appRoutesModule.getContract()
);

const completeBundleModule = new CompleteBundleModule(
    userAuthModule.getContract(),
    routerModule.getContract(),
    paymentModule.getContract(),
    upsellModule.getContract(),
    upsellUIModule.getContract(),
    subscriptionModule.getContract(),
    appRoutesModule.getContract()
);

const creativePacksModule = new CreativePacksModule(
    userAuthModule.getContract(),
    routerModule.getContract(),
    paymentModule.getContract(),
    upsellModule.getContract(),
    upsellUIModule.getContract(),
    subscriptionModule.getContract(),
    appRoutesModule.getContract()
);

const completeAiPackModule = new CompleteAiPackModule(
    userAuthModule.getContract(),
    routerModule.getContract(),
    paymentModule.getContract(),
    upsellModule.getContract(),
    upsellUIModule.getContract(),
    subscriptionModule.getContract(),
    appRoutesModule.getContract(),
    claudeWorkBundleModule.getContract()
);

const upsellAnalyticsModule = new UpsellAnalyticsModule(
    upsellModule.getContract(),
    upsellUIModule.getContract(),
    personalBoostPackModule.getContract(),
    aiToolsPackModule.getContract(),
    ultimateAiPackModule.getContract(),
    completeAiPackModule.getContract(),
    claudeWorkBundleModule.getContract(),
    completeBundleModule.getContract(),
    creativePacksModule.getContract(),
    appRoutesModule.getContract(),
    routerModule.getContract(),
    userAuthModule.getContract(),
);

const assistantModelModule = new AssistantModelModule(routerModule.getContract());
const assistantUIModule = new AssistantUIModule(
    routerModule.getContract(),
    assistantModelModule.getContract(),
    aiToolsPackModule.getContract()
);
const lessonExitConfirmationUiModule = new LessonExitConfirmationUiModule();

const interactivePromptModelModule = new InteractivePromptModelModule();
const interactivePromptUIModule = new InteractivePromptUIModule();

const challengesModule = new ChallengesModule(
    coursesModule.getContract(),
    enrollmentsModule.getContract(),
    routerModule.getContract()
);
const promptsLibraryModule = new PromptsLibraryModule(
    appRoutesModule.getContract(),
    appModule.getContract(),
    routerModule.getContract()
);
const promptsLibraryUIModule = new PromptsLibraryUIModule(
    promptsLibraryModule.getContract(),
    routerModule.getContract(),
    appRoutesModule.getContract(),
    currentAiTipModule.getContract(),
    personalBoostPackModule.getContract(),
    ultimateAiPackModule.getContract(),
    completeAiPackModule.getContract(),
    claudeWorkBundleModule.getContract(),
    upsellModule.getContract()
);

const promptsLibraryAnalyticsModule = new PromptsLibraryAnalyticsModule(
    promptsLibraryModule.getContract(),
    promptsLibraryUIModule.getContract(),
    appRoutesModule.getContract()
);

const browserInstructionModule = new BrowserInstructionModule(
    routerModule.getContract(),
    appRoutesModule.getContract(),
    platformContextModule.getContract(),
    productOnboardingModule.getContract(),
    userAuthModule.getContract()
);

const reEngagementModule = new ReEngagementModule(lastEnrollmentModule.getContract(), metadataModule.getContract(), streakModule.getContract());

const appTourModule = new AppTourModule(
    userAuthModule.getContract(),
    appRoutesModule.getContract(),
    metadataModule.getContract(),
    amplitudeFlagsModule.getContract(),
    productOnboardingModule.getContract()
);

const lessonCompletionModule = new LessonCompletionModule(
    userAuthModule.getContract(),
    lessonsModule.getContract(),
    enrollmentsModule.getContract(),
    streakModule.getContract(),
    topicsModule.getContract(),
    coursesModule.getContract()
);
const xpModule = new XpModule(
    metadataModule.getContract(),
    taskLayoutUIModule.getContract(),
    tasksModule.getContract(),
    lessonCompletionModule.getContract(),
    userAuthModule.getContract(),
    amplitudeFlagsModule.getContract()
);
const lessonCompletionFlowModule = new LessonCompletionFlowModule(
    lessonCompletionModule.getContract(),
    currentLessonModule.getContract(),
    currentCourseModule.getContract(),
    currentTopicModule.getContract(),
    lessonsModule.getContract(),
    lessonUiModule.getContract(),
    lessonFeedbackModule.getContract(),
    dailyGoalStreakModule.getContract(),
    amplitudeFlagsModule.getContract(),
    platformContextModule.getContract(),
    productOnboardingModule.getContract(),
    routerModule.getContract(),
    xpModule.getContract()
);
const lessonTransitionModule = new LessonTransitionModule(
    lessonCompletionModule.getContract(),
    lessonCompletionFlowModule.getContract(),
    currentLessonModule.getContract(),
    currentCourseModule.getContract(),
    lessonsModule.getContract(),
    amplitudeFlagsModule.getContract(),
    userAuthModule.getContract()
);
const soundModule = new SoundModule(
    taskLayoutUIModule.getContract(),
    tasksModule.getContract(),
    lessonCompletionModule.getContract(),
    lessonCompletionFlowModule.getContract(),
    currentLessonModule.getContract(),
    amplitudeFlagsModule.getContract()
);
const themeModule = new ThemeModule(
    userAuthModule.getContract(),
    metadataModule.getContract(),
    amplitudeFlagsModule.getContract()
);

export const FEATURES = new ModulesGroup([
    userAuthModule,
    signInFormModule,
    enrollmentsModule,
    coursesModule,
    topicsModule,
    lessonsModule,
    tasksModule,
    currentCourseModule,
    currentTopicModule,
    currentLessonModule,
    routerModule,
    appRoutesModule,
    tokenStorageModule,
    appModule,
    notificationModule,
    pageTypeModule,
    aiTipsModule,
    currentAiTipModule,
    onboardingModule,
    currentOnboardingScreenModule,
    paymentModule,
    landingModule,
    landingProfessionFormModule,
    subscriptionModule,
    platformContextModule,
    flagsmithModule,
    amplitudeFlagsModule,
    creatorLandingModule,
    luckLandingModule,
    upsellUIModule,
    upsellModule,
    productOnboardingModule,
    streakModule,
    streakWidgetModule,
    dailyGoalStreakModule,
    productOnboardingEventsModule,
    metadataModule,
    dailyTaskModule,
    dailyTaskUiModule,
    filteredCoursesModule,
    roadmapsModule,
    roadmapCertificateProgramModule,
    lastEnrollmentModule,
    assistantUIModule,
    assistantModelModule,
    registrationModule,
    lessonFeedbackModule,
    certificateModalModule,
    ultimateAiPackModule,
    completeAiPackModule,
    personalBoostPackModule,
    aiToolsPackModule,
    claudeWorkBundleModule,
    completeBundleModule,
    creativePacksModule,
    upsellAnalyticsModule,
    lessonUiModule,
    lessonExitConfirmationUiModule,
    taskLayoutUIModule,
    interactivePromptModelModule,
    interactivePromptUIModule,
    challengesModule,
    promptsLibraryModule,
    promptsLibraryUIModule,
    promptsLibraryAnalyticsModule,
    browserInstructionModule,
    reEngagementModule,
    appTourModule,
    lessonCompletionModule,
    xpModule,
    lessonCompletionFlowModule,
    lessonTransitionModule,
    soundModule,
    themeModule
]);

FEATURES.run();

if (typeof window !== "undefined") {
    window.features = FEATURES;
}
